/**
 * Serviço de Utilidades
 * Funções auxiliares para formatação, conversão e manipulação de dados
 */

/**
 * Formata um hash para exibição abreviada
 * @param {string} hash - Hash completo
 * @param {number} tamanhoPrefixo - Quantidade de caracteres no início
 * @param {number} tamanhoSufixo - Quantidade de caracteres no final
 * @returns {string} Hash abreviado
 */
export function formatarHash(hash, tamanhoPrefixo = 6, tamanhoSufixo = 6) {
  if (!hash || hash.length <= tamanhoPrefixo + tamanhoSufixo + 3) {
    return hash;
  }
  return `${hash.substring(0, tamanhoPrefixo)}...${hash.substring(hash.length - tamanhoSufixo)}`;
}

/**
 * Returns the current network coin symbol from the app store or window global.
 * @returns {string}
 */
function getNetworkCoin() {
  // Try reading from Pinia store via window (set by App.vue on mount)
  return window.__QUARNX_COIN__ || 'tKinX'
}

/**
 * Converts a timestamp to a Date object, handling both seconds and milliseconds.
 * API returns Unix timestamps in seconds (10 digits). JS Date expects milliseconds.
 * @param {number|string|Date} ts - Timestamp (seconds or milliseconds) or Date
 * @returns {Date} Valid Date object, or current date if invalid
 */
export function parseTimestamp(ts) {
  if (!ts) return new Date()
  if (ts instanceof Date) return ts
  const num = typeof ts === 'string' ? parseInt(ts) : ts
  if (isNaN(num) || num <= 0) return new Date()
  // 10 digits = seconds, 13 digits = milliseconds
  return num < 10000000000 ? new Date(num * 1000) : new Date(num)
}

/**
 * Formata um valor para exibição com unidade de token
 * @param {number|string} valor - Valor a ser formatado
 * @param {string} simbolo - Símbolo do token (auto-detected from network if not provided)
 * @param {number} casasDecimais - Casas decimais para arredondamento
 * @returns {string} Valor formatado
 */
export function formatarValorToken(valor, simbolo = null, casasDecimais = 4) {
  if (valor === undefined || valor === null) return '-';
  
  const coin = simbolo || getNetworkCoin()
  let valorNumerico = parseFloat(valor);
  if (isNaN(valorNumerico)) return '-';
  
  // O kernel retorna valores em nanoKinX (1 KinX = 10^9 nanoKinX)
  // Converter automaticamente se o valor parecer estar em nanoKinX (> 1 bilhão)
  if (valorNumerico >= 1_000_000_000) {
    valorNumerico = valorNumerico / 1_000_000_000;
  }
  
  const valorFormatado = valorNumerico.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: casasDecimais
  });
  
  return `${valorFormatado} ${coin}`;
}

// ═══════════════════════════════════════════
// FormatadorNano — Conversão Inteligente de nanoKinX
// ═══════════════════════════════════════════

/**
 * Tabela de faixas para conversão inteligente de nanoKinX.
 * Cada faixa define: limite superior (exclusivo), divisor, e prefixo de unidade.
 */
const NANO_THRESHOLDS = [
  { limit: 1_000,           divisor: 1,               prefix: 'nano' },
  { limit: 1_000_000,       divisor: 1_000,           prefix: 'micro' },
  { limit: 1_000_000_000,   divisor: 1_000_000,       prefix: 'milli' },
  { limit: Infinity,        divisor: 1_000_000_000,   prefix: ''      },
]

/**
 * Converte um valor em nanoKinX para a unidade de exibição mais legível.
 * 
 * Faixas:
 *   0              → "0 tKinX"
 *   1–999          → "N nano tKinX"
 *   1.000–999.999  → "N micro tKinX"
 *   1M–999.999.999 → "N milli tKinX"
 *   ≥1B            → "N tKinX"
 *
 * @param {number} valorNano - Valor em nanoKinX (inteiro ≥ 0)
 * @param {string|null} simboloMoeda - Símbolo da moeda (null = auto-detect da rede)
 * @param {number} casasDecimais - Máximo de casas decimais (padrão: 4)
 * @returns {string} Valor formatado com unidade e símbolo
 */
export function formatarNano(valorNano, simboloMoeda = null, casasDecimais = 4) {
  const coin = simboloMoeda || getNetworkCoin()

  if (valorNano === null || valorNano === undefined) return '-'
  const valor = Number(valorNano)
  if (isNaN(valor) || valor < 0) return '-'
  if (valor === 0) return `0 ${coin}`

  const faixa = NANO_THRESHOLDS.find(f => valor < f.limit)
  const convertido = valor / faixa.divisor
  const formatado = convertido.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: casasDecimais,
  })

  const prefixo = faixa.prefix ? `${faixa.prefix} ` : ''
  return `${formatado} ${prefixo}${coin}`
}

/**
 * Mapa de prefixos para multiplicadores (inverso do NANO_THRESHOLDS).
 */
const PREFIX_MULTIPLIERS = {
  'nano': 1,
  'micro': 1_000,
  'milli': 1_000_000,
  '': 1_000_000_000,
}

/**
 * Parser inverso: converte uma string formatada por formatarNano de volta para nanoKinX.
 * Aceita formatos como "500 nano tKinX", "1,5 micro tKinX", "10 tKinX".
 *
 * @param {string} displayStr - String formatada por formatarNano
 * @returns {number} Valor em nanoKinX, ou NaN se inválido
 */
export function parseNanoDisplay(displayStr) {
  if (!displayStr || displayStr === '-') return NaN
  const str = displayStr.trim()

  // Extrair: "123,45 [prefix] COIN"
  // Regex: número (com vírgula/ponto decimal) + espaço + [prefix espaço] + coin
  const match = str.match(/^([\d.,]+)\s+(nano|micro|milli)?\s*(.+)$/)
  if (!match) return NaN

  const [, numStr, prefix] = match
  // Converter número pt-BR (vírgula decimal) para float
  const numNormalized = numStr.replace(/\./g, '').replace(',', '.')
  const num = parseFloat(numNormalized)
  if (isNaN(num)) return NaN

  const multiplier = PREFIX_MULTIPLIERS[prefix || '']
  if (multiplier === undefined) return NaN

  return Math.round(num * multiplier)
}

/**
 * Formata uma data para exibição
 * @param {Date|string|number} data - Data a ser formatada
 * @param {boolean} incluirHora - Se deve incluir a hora
 * @returns {string} Data formatada
 */
export function formatarData(data, incluirHora = true) {
  if (!data) return '-';
  
  const dataObj = parseTimestamp(data);
  if (isNaN(dataObj.getTime())) return '-';
  
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  };
  
  if (incluirHora) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.second = '2-digit';
  }
  
  return dataObj.toLocaleString('pt-BR', options);
}

/**
 * Formata um número com separadores locais
 * @param {number|string} numero - Número a ser formatado
 * @param {number} casasDecimais - Casas decimais para arredondamento (padrão: 2)
 * @param {boolean} abreviar - Se deve abreviar números grandes (K, M, B)
 * @returns {string} Número formatado
 */
export function formatarNumero(numero, casasDecimais = 2, abreviar = false) {
  if (numero === undefined || numero === null) return '-';
  
  const numeroFloat = parseFloat(numero);
  if (isNaN(numeroFloat)) return '-';
  
  // Se a opção de abreviar estiver ativada
  if (abreviar) {
    if (numeroFloat >= 1000000000) {
      return `${(numeroFloat / 1000000000).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}B`;
    }
    if (numeroFloat >= 1000000) {
      return `${(numeroFloat / 1000000).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}M`;
    }
    if (numeroFloat >= 1000) {
      return `${(numeroFloat / 1000).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}K`;
    }
  }
  
  return numeroFloat.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: casasDecimais
  });
}

/**
 * Converte um valor em segundos para formato legível
 * @param {number} segundos - Tempo em segundos
 * @returns {string} Tempo formatado
 */
export function formatarTempo(segundos) {
  if (segundos === undefined || segundos === null || isNaN(segundos)) return '-';
  
  if (segundos < 60) {
    return `${segundos.toFixed(1)} segundos`;
  } else if (segundos < 3600) {
    const minutos = segundos / 60;
    return `${minutos.toFixed(1)} minutos`;
  } else if (segundos < 86400) {
    const horas = segundos / 3600;
    return `${horas.toFixed(1)} horas`;
  } else {
    const dias = segundos / 86400;
    return `${dias.toFixed(1)} dias`;
  }
}

/**
 * Converte tempo relativo (ex: "há 5 minutos")
 * @param {Date|string|number} data - Data para calcular o tempo relativo
 * @returns {string} Tempo relativo
 */
export function tempoRelativo(data) {
  if (!data) return '-';
  
  const dataObj = parseTimestamp(data);
  if (isNaN(dataObj.getTime())) return '-';
  
  // If the date is before year 2000, it's likely an unset/epoch timestamp
  if (dataObj.getFullYear() < 2000) return 'ativo';
  
  const agora = new Date();
  const diffMs = agora - dataObj;
  const diffSeg = Math.floor(diffMs / 1000);
  
  // Future dates or very recent
  if (diffSeg < 0) return 'agora';
  if (diffSeg < 60) {
    return 'agora mesmo';
  } else if (diffSeg < 3600) {
    const minutos = Math.floor(diffSeg / 60);
    return `há ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`;
  } else if (diffSeg < 86400) {
    const horas = Math.floor(diffSeg / 3600);
    return `há ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  } else if (diffSeg < 2592000) { // 30 dias
    const dias = Math.floor(diffSeg / 86400);
    return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  } else if (diffSeg < 31536000) { // 365 dias
    const meses = Math.floor(diffSeg / 2592000);
    return `há ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  } else {
    const anos = Math.floor(diffSeg / 31536000);
    return `há ${anos} ${anos === 1 ? 'ano' : 'anos'}`;
  }
}

/**
 * Formata um número de bytes para exibição legível
 * @param {number} bytes - Tamanho em bytes
 * @param {number} casasDecimais - Casas decimais para arredondamento
 * @returns {string} Tamanho formatado
 */
export function formatarBytes(bytes, casasDecimais = 2) {
  if (bytes === 0) return '0 Bytes';
  if (!bytes || isNaN(bytes)) return '-';
  
  const k = 1024;
  const unidades = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(casasDecimais))} ${unidades[i]}`;
}

/**
 * Formata um número grande para exibição legível
 * @param {number} numero - Número a ser formatado
 * @param {number} casasDecimais - Casas decimais para arredondamento
 * @returns {string} Número formatado
 */
export function formatarNumeroGrande(numero, casasDecimais = 0) {
  if (numero === undefined || numero === null || isNaN(numero)) return '-';
  
  // Usar formatarNumero com abreviação
  return formatarNumero(numero, casasDecimais, true);
}

/**
 * Formata um percentual para exibição
 * @param {number} valor - Valor percentual
 * @param {number} casasDecimais - Casas decimais para arredondamento
 * @returns {string} Percentual formatado
 */
export function formatarPercentual(valor, casasDecimais = 2) {
  if (valor === undefined || valor === null || isNaN(valor)) return '-';
  
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: casasDecimais
  })}%`;
}

/**
 * Gera um gradiente de cores com base em um valor
 * @param {number} valor - Valor entre 0 e 1
 * @param {string} corMinima - Cor para valor mínimo
 * @param {string} corMaxima - Cor para valor máximo
 * @returns {string} Cor em formato hexadecimal
 */
export function corGradiente(valor, corMinima = '#ff0000', corMaxima = '#00ff00') {
  // Limita valor entre 0 e 1
  const v = Math.max(0, Math.min(1, valor));
  
  // Converte cores para RGB
  const rMin = parseInt(corMinima.substring(1, 3), 16);
  const gMin = parseInt(corMinima.substring(3, 5), 16);
  const bMin = parseInt(corMinima.substring(5, 7), 16);
  
  const rMax = parseInt(corMaxima.substring(1, 3), 16);
  const gMax = parseInt(corMaxima.substring(3, 5), 16);
  const bMax = parseInt(corMaxima.substring(5, 7), 16);
  
  // Calcula cor intermediária
  const r = Math.round(rMin + (rMax - rMin) * v);
  const g = Math.round(gMin + (gMax - gMin) * v);
  const b = Math.round(bMin + (bMax - bMin) * v);
  
  // Converte para hexadecimal
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Gera cores para visualização de gráficos
 * @param {number} quantidade - Quantidade de cores a gerar
 * @param {number} opacidade - Opacidade das cores (0-1)
 * @returns {Array} Lista de cores
 */
export function gerarCoresVisualizacao(quantidade, opacidade = 1) {
  const coresPredefinidas = [
    `rgba(126, 87, 194, ${opacidade})`, // Roxo (primário)
    `rgba(3, 169, 244, ${opacidade})`,  // Azul claro (secundário)
    `rgba(255, 64, 129, ${opacidade})`, // Rosa (accent)
    `rgba(76, 175, 80, ${opacidade})`,  // Verde
    `rgba(255, 193, 7, ${opacidade})`,  // Amarelo
    `rgba(255, 82, 82, ${opacidade})`,  // Vermelho
    `rgba(33, 150, 243, ${opacidade})`, // Azul
    `rgba(233, 30, 99, ${opacidade})`,  // Rosa escuro
    `rgba(156, 39, 176, ${opacidade})`, // Roxo escuro
    `rgba(0, 188, 212, ${opacidade})`,  // Ciano
    `rgba(255, 152, 0, ${opacidade})`,  // Laranja
    `rgba(121, 85, 72, ${opacidade})`,  // Marrom
  ];
  
  // Se temos cores suficientes, retorna as predefinidas
  if (quantidade <= coresPredefinidas.length) {
    return coresPredefinidas.slice(0, quantidade);
  }
  
  // Caso contrário, gera cores aleatórias
  const cores = [...coresPredefinidas];
  
  for (let i = coresPredefinidas.length; i < quantidade; i++) {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    cores.push(`rgba(${r}, ${g}, ${b}, ${opacidade})`);
  }
  
  return cores;
}

// Funções auxiliares para manipulação de JSON-RPC
export const JsonRpc = {
  /**
   * Cria uma requisição JSON-RPC
   * @param {string} metodo - Nome do método
   * @param {Array} parametros - Parâmetros do método
   * @returns {Object} Objeto de requisição JSON-RPC
   */
  criarRequisicao: (metodo, parametros = []) => {
    return {
      jsonrpc: '2.0',
      id: new Date().getTime(),
      method: metodo,
      params: parametros
    };
  },
  
  /**
   * Verifica se uma resposta JSON-RPC é válida
   * @param {Object} resposta - Resposta a verificar
   * @returns {boolean} Se a resposta é válida
   */
  verificarResposta: (resposta) => {
    return resposta && 
           resposta.hasOwnProperty('jsonrpc') && 
           resposta.jsonrpc === '2.0' &&
           resposta.hasOwnProperty('id');
  },
  
  /**
   * Verifica se uma resposta contém erro
   * @param {Object} resposta - Resposta a verificar
   * @returns {boolean} Se a resposta contém erro
   */
  contemErro: (resposta) => {
    return JsonRpc.verificarResposta(resposta) && resposta.hasOwnProperty('error');
  },
  
  /**
   * Extrai o resultado de uma resposta JSON-RPC
   * @param {Object} resposta - Resposta JSON-RPC
   * @returns {any} Resultado da resposta
   */
  extrairResultado: (resposta) => {
    if (!JsonRpc.verificarResposta(resposta)) {
      throw new Error('Resposta JSON-RPC inválida');
    }
    
    if (JsonRpc.contemErro(resposta)) {
      throw new Error(resposta.error.message || 'Erro na resposta JSON-RPC');
    }
    
    return resposta.result;
  }
};

// Exporta todas as funções
export default {
  formatarHash,
  formatarValorToken,
  formatarNano,
  parseNanoDisplay,
  formatarData,
  formatarTempo,
  tempoRelativo,
  parseTimestamp,
  formatarBytes,
  formatarNumeroGrande,
  formatarPercentual,
  corGradiente,
  gerarCoresVisualizacao,
  JsonRpc
};
